import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight, ExternalLink } from "lucide-react";
import PageShell from "../components/sections/PageShell";
import projects from "../data/projects";
import { Tape } from "../components/brand/Doodles";

const FEATURED_SLUGS = new Set([
  "ezbuildabot",
  "gadget-guys-smart-home",
  "ezscribe",
  "family-health-care-of-gallatin",
]);

const setMeta = (attr, key, value) => {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute("content", value);
};
const setLink = (rel, href) => {
  let el = document.querySelector(`link[rel="${rel}"]`);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
};

export default function ProjectsPage() {
  useEffect(() => {
    document.title = `Things I've Actually Built — ${projects.length} projects · Gadget Guy Media`;
    window.scrollTo(0, 0);
    const desc =
      `Every AI app, tool, and site shipped by Gadget Guy Media — ${projects.length} live projects including ezScribe, ezBuildabot, ezItemizer, Glitch, The Pairing Mode, Claude Code Cost Tracker, and more. Built by one person with AI as the co-pilot.`;
    setMeta("name", "description", desc);
    setMeta("property", "og:title", "Things I've Actually Built — Gadget Guy Media");
    setMeta("property", "og:description", desc);
    setMeta("property", "og:url", "https://gadgetguymedia.com/projects");
    setMeta("name", "twitter:title", "Things I've Actually Built — Gadget Guy Media");
    setMeta("name", "twitter:description", desc);
    setLink("canonical", "https://gadgetguymedia.com/projects");
    return () => setLink("canonical", "https://gadgetguymedia.com/");
  }, []);

  const featured = projects.filter((p) => FEATURED_SLUGS.has(p.slug));
  const rest = projects.filter((p) => !FEATURED_SLUGS.has(p.slug));

  return (
    <PageShell testId="projects-page">
      {/* Hero */}
      <section
        className="relative"
        style={{
          paddingTop: "clamp(48px, 8vh, 96px)",
          paddingBottom: "clamp(48px, 8vh, 96px)",
        }}
      >
        <div
          aria-hidden
          className="pointer-events-none absolute inset-0"
          style={{
            backgroundImage:
              "radial-gradient(1000px 500px at 18% 30%, rgba(11,60,220,0.16), transparent 70%), radial-gradient(800px 500px at 90% 20%, rgba(40,200,240,0.10), transparent 70%)",
          }}
        />
        <div className="relative max-w-7xl mx-auto px-5 md:px-8">
          <div className="max-w-3xl">
            <div className="ggm-eyebrow mb-4" data-testid="projects-eyebrow">
              The Receipts
            </div>
            <h1
              className="font-condensed font-bold text-white uppercase"
              style={{
                fontSize: "clamp(2.5rem, 6vw, 4.75rem)",
                lineHeight: 0.98,
                letterSpacing: "-0.025em",
              }}
              data-testid="projects-heading"
            >
              Things I've <span className="ggm-gradient-text">Actually</span> Built.
            </h1>
            <p className="mt-6 font-body text-white/70 text-base md:text-lg leading-relaxed max-w-2xl">
              AI agents, applications, automations, websites, and systems built to solve
              real problems. {projects.length} live projects — every one shipped by one
              person using AI as the development team.
            </p>
          </div>
        </div>
      </section>

      {/* Featured — editorial alternating */}
      <section
        className="relative"
        style={{ paddingBottom: "clamp(48px, 8vh, 96px)" }}
        data-testid="projects-featured"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="ggm-eyebrow mb-8">Featured builds</div>
          <div className="space-y-14 md:space-y-20">
            {featured.map((p, i) => (
              <FeaturedCase key={p.slug} project={p} reverse={i % 2 === 1} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Full library — editorial list */}
      <section
        className="relative"
        style={{ paddingBottom: "clamp(48px, 8vh, 96px)" }}
        data-testid="projects-library"
      >
        <div className="max-w-7xl mx-auto px-5 md:px-8">
          <div className="ggm-eyebrow mb-6">The full library · {projects.length} projects</div>
          <ul
            className="divide-y border-y"
            style={{ borderColor: "var(--ggm-border-subtle)" }}
          >
            {rest.map((p, idx) => (
              <li key={p.slug} className="relative">
                <Link
                  to={`/projects/${p.slug}`}
                  className="group flex items-center justify-between gap-4 md:gap-6 py-5 md:py-6 px-1 md:px-3 transition-colors hover:bg-white/[0.02]"
                  data-testid={`project-link-${idx}`}
                >
                  <div className="flex items-center gap-4 md:gap-6 min-w-0">
                    <span
                      className="font-condensed text-white/30 tabular-nums text-xs md:text-sm"
                      style={{ letterSpacing: "0.05em" }}
                    >
                      {String(idx + 1 + featured.length).padStart(2, "0")}
                    </span>
                    {p.image && (
                      <div
                        className="hidden sm:block shrink-0 overflow-hidden rounded-md"
                        style={{
                          width: 128,
                          height: 72,
                          background: "var(--ggm-surface)",
                          border: "1px solid var(--ggm-border-subtle)",
                        }}
                      >
                        <img
                          src={p.image}
                          alt=""
                          loading="lazy"
                          aria-hidden="true"
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.05]"
                        />
                      </div>
                    )}
                    <div className="min-w-0">
                      <h2
                        className="font-condensed font-bold text-white truncate group-hover:text-[var(--ggm-signal-cyan)] transition-colors"
                        style={{
                          fontSize: "clamp(1.25rem, 2.6vw, 1.85rem)",
                          letterSpacing: "-0.01em",
                        }}
                      >
                        {p.name}
                      </h2>
                      <div className="font-condensed uppercase tracking-widest text-[10px] md:text-[11px] text-white/40 mt-1">
                        {p.category} · {p.year}
                      </div>
                    </div>
                  </div>
                  <ArrowUpRight
                    size={22}
                    className="shrink-0 text-white/40 transition-all duration-300 group-hover:text-[var(--ggm-signal-cyan)] group-hover:-translate-y-0.5 group-hover:translate-x-0.5"
                  />
                </Link>
                {p.url && (
                  <a
                    href={p.url}
                    target="_blank"
                    rel="noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className="absolute right-12 md:right-16 top-1/2 -translate-y-1/2 hidden md:inline-flex items-center gap-1.5 font-condensed uppercase tracking-widest text-[10px] text-white/50 hover:text-[var(--ggm-signal-cyan)] transition-colors px-2.5 py-1 rounded-full border"
                    style={{ borderColor: "var(--ggm-border-subtle)" }}
                    data-testid={`project-live-${idx}`}
                    aria-label={`Visit ${p.name} live`}
                  >
                    Live <ExternalLink size={11} />
                  </a>
                )}
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* Conversion */}
      <section
        className="relative"
        style={{
          paddingTop: "clamp(64px, 10vh, 140px)",
          paddingBottom: "clamp(64px, 10vh, 140px)",
          background:
            "radial-gradient(1000px 500px at 30% 20%, rgba(11,60,220,0.14), transparent 70%)",
        }}
        data-testid="projects-conversion"
      >
        <div className="max-w-4xl mx-auto px-5 md:px-8 text-center">
          <h2
            className="font-condensed font-bold text-white uppercase"
            style={{
              fontSize: "clamp(2rem, 4.5vw, 3.25rem)",
              lineHeight: 1.02,
              letterSpacing: "-0.02em",
            }}
          >
            Got Something In Your Head?
          </h2>
          <p
            className="mt-4 font-condensed font-bold ggm-gradient-text uppercase"
            style={{
              fontSize: "clamp(1.25rem, 3vw, 2rem)",
              lineHeight: 1.15,
              letterSpacing: "-0.01em",
            }}
          >
            Let's turn it into something real.
          </p>
          <div className="mt-8 flex justify-center">
            <Link
              to="/#start-a-project"
              className="ggm-btn-primary"
              data-testid="projects-cta"
            >
              Start a Project <ArrowUpRight size={16} />
            </Link>
          </div>
        </div>
      </section>
    </PageShell>
  );
}

/** Alternating editorial featured case — inspired by the homepage Work section. */
const FeaturedCase = ({ project: p, reverse, index }) => {
  return (
    <article
      className="grid gap-6 md:gap-10 lg:grid-cols-2 items-center"
      style={{ direction: reverse ? "rtl" : "ltr" }}
      data-testid={`project-featured-${p.slug}`}
    >
      <div style={{ direction: "ltr" }}>
        <div className="ggm-eyebrow mb-3">
          Featured · {String(index + 1).padStart(2, "0")}
        </div>
        <h2
          className="font-condensed font-bold text-white"
          style={{
            fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)",
            lineHeight: 1.05,
            letterSpacing: "-0.015em",
          }}
        >
          {p.name}
        </h2>
        <p
          className="mt-3 ggm-gradient-text font-condensed font-bold"
          style={{ fontSize: "clamp(1.05rem, 2vw, 1.35rem)", lineHeight: 1.25 }}
        >
          {p.tagline}
        </p>
        <p className="mt-4 font-body text-white/75 text-base leading-relaxed max-w-xl">
          {p.summary}
        </p>
        <div className="mt-6 flex flex-wrap gap-3">
          <Link
            to={`/projects/${p.slug}`}
            className="ggm-btn-secondary"
            data-testid={`project-featured-case-${p.slug}`}
          >
            Read case study <ArrowRight size={16} />
          </Link>
          {p.url && (
            <a
              href={p.url}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 font-condensed uppercase tracking-widest text-xs text-white/70 hover:text-[var(--ggm-signal-cyan)] transition-colors"
              style={{ transitionDuration: "var(--ggm-duration-ui)", minHeight: 44 }}
            >
              Visit live <ArrowUpRight size={13} />
            </a>
          )}
        </div>
      </div>
      <Link
        to={`/projects/${p.slug}`}
        style={{ direction: "ltr", transform: `rotate(${reverse ? 1.4 : -1.4}deg)` }}
        className="group ggm-polaroid relative block"
        aria-label={`${p.name} case study`}
      >
        <Tape />
        <div className="aspect-[16/10] w-full overflow-hidden" style={{ background: "var(--ggm-tech-light)" }}>
          {p.image && (
            <img
              src={p.image}
              alt={`${p.name} — ${p.tagline}`}
              loading="lazy"
              decoding="async"
              className="w-full h-full object-cover object-top"
            />
          )}
        </div>
      </Link>
    </article>
  );
};
